// Helpers do motor de renderização de campos personalizados (organization fields).
//
// Centraliza as regras que hoje estão duplicadas nas ~9 telas com `returnFields()`:
// normalização do valor por tipo, obrigatoriedade efetiva (override por status) e
// montagem do payload de save. Mantido puro (sem React) pra ser testável e reusável.

// Espelha o enum OrganizationFieldType do backend (FieldService.Business.Models).
export const FIELD_TYPE = {
  TEXT: 1,
  RADIO: 2,
  CHECKBOX: 3,
  SELECT: 4,
  NUMBER: 5,
  SELECT_MULTIPLE: 6,
  TEXT_MULTIPLE_LINE: 7,
  DECIMAL: 8,
  DATE: 9,
  REGEX_TEXT: 10,
};

// Tipos cujo valor é escolhido a partir de uma lista de opções.
export const isOptionType = (type) =>
  type === FIELD_TYPE.RADIO || type === FIELD_TYPE.SELECT || type === FIELD_TYPE.SELECT_MULTIPLE;

/**
 * Obrigatoriedade EFETIVA do campo: parte de `isrequired` e aplica o override por status
 * (tb_organization_field_status). Mesma regra do backend, replicada pra UX (o backend valida de novo).
 */
export const isFieldRequired = (field, calledStatusId) => {
  if (!field || !field.isrequired) return false;
  const statuses = field.organizationfieldstatuses || [];
  const override = statuses.find((s) => s.statusid === calledStatusId);
  if (override && override.required === false) return false;
  return true;
};

/** O campo aceita edição? Respeita `allow_editing` da definição, além da flag global da organização. */
export const isFieldEditable = (field) => (field && field.allowediting) === true;

const pad2 = (n) => String(n).padStart(2, '0');

/** Date -> 'yyyy-MM-dd' (formato aceito pelo backend, sem fuso). */
export const toIsoDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** Converte o valor persistido (string) em Date, aceitando 'dd/MM/yyyy' e 'yyyy-MM-dd'. */
export const parseDateValue = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === 'string' && raw.includes('/')) {
    const [day, month, year] = raw.split('/');
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(`${raw}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Estado inicial de UM campo a partir do registro já salvo (pode não existir: campo sem valor).
 * Retorna sempre a mesma forma, independente do tipo — o motor lê daqui.
 *   { value, optionId, multipleIds }
 */
export const buildInitialFieldValue = (field, savedRecords) => {
  const type = field.organizationfieldtype;
  const records = (savedRecords || []).filter(
    (r) => Number(r.organizationfieldid) === Number(field.id)
  );

  if (type === FIELD_TYPE.SELECT_MULTIPLE) {
    const multipleIds = records
      .map((r) => r.organizationfieldoptionsvaluesid)
      .filter((id) => id !== null && id !== undefined)
      .map(Number);
    return { value: records.map((r) => r.value).filter(Boolean).join(','), optionId: null, multipleIds };
  }

  const record = records[0];
  if (!record) return { value: '', optionId: null, multipleIds: [] };

  const rawValue = record.value === null || record.value === undefined || record.value === 'null'
    ? ''
    : String(record.value);

  return {
    value: rawValue,
    optionId: record.organizationfieldoptionsvaluesid ?? null,
    multipleIds: [],
  };
};

/** O campo está vazio? (usado pra bloquear o save de campo obrigatório em branco) */
export const isFieldValueEmpty = (field, fieldValue) => {
  const type = field.organizationfieldtype;
  if (type === FIELD_TYPE.SELECT_MULTIPLE) {
    return !fieldValue || !fieldValue.multipleIds || fieldValue.multipleIds.length === 0;
  }
  // Checkbox nunca é "vazio": 'false' é uma resposta válida.
  if (type === FIELD_TYPE.CHECKBOX) return false;
  return !fieldValue || !String(fieldValue.value || '').trim();
};

/** Monta o payload do endpoint PUT /calledorganizationfields/quick-treat. */
export const buildSavePayload = (calledId, field, fieldValue) => ({
  calledId: Number(calledId),
  organizationFieldId: Number(field.id),
  value: fieldValue.value === null || fieldValue.value === undefined ? '' : String(fieldValue.value),
  organizationFieldOptionsValuesId:
    fieldValue.optionId !== null && fieldValue.optionId !== undefined ? Number(fieldValue.optionId) : null,
  multipleOptionIds:
    field.organizationfieldtype === FIELD_TYPE.SELECT_MULTIPLE ? (fieldValue.multipleIds || []) : [],
});
